/**
 * Piezas visuales básicas de Softfi.
 *
 * Todo lo que se repite en más de una pantalla vive aquí: botones, campos,
 * tarjetas, etiquetas. Así el diseño es consistente sin esfuerzo y arreglar un
 * detalle visual se hace en un solo lugar.
 *
 * Nota importante sobre tamaños: los botones y campos tienen una altura mínima
 * de 44px. No es capricho — es el tamaño mínimo que un dedo acierta de forma
 * fiable en una pantalla táctil. Un POS con botones de 32px hace que el cajero
 * se equivoque, y en caja equivocarse cuesta dinero.
 */

import { Children, forwardRef, Fragment, isValidElement, useCallback, useId, useLayoutEffect, useRef, useState, type ButtonHTMLAttributes, type ChangeEvent, type InputHTMLAttributes, type ReactNode, type TextareaHTMLAttributes } from "react";
import { ArrowRight, Check, ChevronLeft, Loader2, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { Desplegable, type OpcionDesplegable } from "@/components/Desplegable";
import { normalizarDecimal } from "@/lib/decimales";
import { lecturaDeMonto, type FormatoMoneda } from "@/lib/money";
import { comoEtiqueta } from "@/lib/texto";
import { BotonDeAyuda } from "@/features/ayuda/BotonDeAyuda";

/** Une clases CSS ignorando las vacías. */
/**
 * Pega clases de CSS, saltándose las vacías.
 *
 * ⚠️ NO RESUELVE CONFLICTOS. Solo las pega una detrás de otra.
 *
 * Si le pasas `cx("w-full", "w-28")` salen las dos en el HTML, y cuál gana lo
 * decide el orden del CSS de Tailwind — no el orden en que las escribiste. Aquí
 * ganó `w-full` y un desplegable se comió el nombre del cliente al vender.
 *
 * ⚠️ AQUÍ DECÍA «no le pases un `w-…` a un Campo, métele una caja alrededor».
 *
 * Era el consejo correcto y no sirvió: volvió a pasar en la pantalla de
 * recetas, donde la casilla de «cuánta lleva» tenía su `w-20` desde el primer
 * día y un buen día se comió el nombre del ingrediente. Nadie tocó esa
 * pantalla — cambió el orden de la hoja de estilos al añadir clases en otra.
 *
 * Una regla que hay que acordarse de cumplir es una regla que se rompe. Ahora
 * lo resuelven `Campo`, `Area` y `Lista`: si les pasas un ancho, no emiten
 * `w-full`. Ver `pidioSuAncho`. Envolver en una caja sigue funcionando igual de
 * bien; lo que ya no hace falta es recordarlo.
 */
export function cx(...clases: (string | false | null | undefined)[]): string {
  return clases.filter(Boolean).join(" ");
}

// =============================================================================
//  BOTÓN
// =============================================================================

type VarianteBoton = "primario" | "secundario" | "fantasma" | "peligro" | "exito";
type TamanoBoton = "sm" | "md" | "lg";

/*
 * Los botones NO llevan sombra.
 *
 * Es el cambio que más distingue un sistema "serio" de uno de plantilla: un
 * botón con sombra parece un botón de juguete flotando sobre la página. El peso
 * lo da el color de fondo y el contraste, que además es lo que se ve bien en la
 * pantalla barata de una tablet de mostrador.
 */
const ESTILOS_BOTON: Record<VarianteBoton, string> = {
  primario:
    "bg-accent text-on-accent hover:bg-accent-hover disabled:bg-faint disabled:text-surface",
  secundario:
    "bg-surface text-ink border border-line-strong hover:bg-surface-hover hover:border-accent/50 disabled:text-faint",
  fantasma: "bg-transparent text-ink-soft hover:bg-surface-hover hover:text-ink",
  peligro: "bg-danger text-white hover:brightness-110",
  exito: "bg-success text-white hover:brightness-110",
};

const TAMANOS_BOTON: Record<TamanoBoton, string> = {
  sm: "h-9 px-3 text-sm gap-1.5",
  md: "h-11 px-4 text-sm gap-2",
  lg: "h-12 px-6 text-base gap-2",
};

export type BotonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variante?: VarianteBoton;
  tamano?: TamanoBoton;
  cargando?: boolean;
  /** Ocupa todo el ancho disponible. En celular casi siempre conviene. */
  ancho?: boolean;
  icono?: ReactNode;
};

export const Boton = forwardRef<HTMLButtonElement, BotonProps>(function Boton(
  {
    variante = "primario",
    tamano = "md",
    cargando = false,
    ancho = false,
    icono,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      // Mientras carga se deshabilita: evita que un doble clic cree dos ventas.
      disabled={disabled || cargando}
      className={cx(
        "inline-flex items-center justify-center rounded-[var(--radius-control)] font-medium",
        "select-none disabled:cursor-not-allowed disabled:opacity-70",
        // `pulsable` (index.css) hunde el botón un 2% al tocarlo. En pantalla
        // táctil es la ÚNICA confirmación que recibe el dedo: no hay puntero ni
        // estado :hover, y el cajero muchas veces tiene las manos ocupadas.
        "pulsable",
        ESTILOS_BOTON[variante],
        TAMANOS_BOTON[tamano],
        ancho && "w-full",
        className,
      )}
      {...props}
    >
      {cargando ? <Loader2 size={16} className="animate-spin" /> : icono}
      {children}
    </button>
  );
});

// =============================================================================
//  CAMPOS DE FORMULARIO
// =============================================================================

/**
 * ¿El que llama ya dijo qué ancho quiere?
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  ⚠️ EL FALLO QUE ESTA FUNCIÓN EXISTE PARA QUE NO VUELVA
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * `BASE_CAMPO` trae `w-full`, que es lo correcto el 95% de las veces. Pero al
 * escribir `<Campo className="w-20">` quedaban LAS DOS clases en el elemento, y
 * ahí Tailwind no funciona como uno espera: no gana la última del marcado, gana
 * la que quede después en la hoja de estilos generada. O sea, gana por azar.
 *
 * Se vio en la pantalla de recetas: la casilla de «cuánta lleva» se estiraba
 * hasta ocupar la fila entera y aplastaba el nombre del ingrediente hasta
 * dejarlo en un par de letras. Y llevaba su `w-20` puesto desde el primer día —
 * lo que cambió fue el orden de la hoja al añadir clases nuevas en otra
 * pantalla. Un fallo que aparece solo, en un sitio que nadie tocó.
 *
 * Con esto, quien pide un ancho concreto lo tiene: `w-full` ni se emite.
 *
 * Se mira `w-` con la palabra completa para no confundirlo con `min-w-` ni
 * `max-w-`, que conviven con `w-full` sin pelearse.
 */
function pidioSuAncho(className?: string): boolean {
  return /(^|\s)w-/.test(className ?? "");
}

const BASE_CAMPO = cx(
  "rounded-[var(--radius-control)] border bg-surface px-3.5 text-ink placeholder:text-faint",
  "border-line-strong transition-colors",
  // El anillo de foco es del color de la marca y bien visible: en un POS el
  // cajero rápido va con Tab y tiene que saber dónde está sin buscarlo.
  "focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30",
  "disabled:bg-surface-2 disabled:text-faint disabled:cursor-not-allowed",
);

export type CampoProps = InputHTMLAttributes<HTMLInputElement> & {
  error?: boolean;
  /**
   * La moneda de una casilla de dinero. Con ella, la casilla enseña cómo
   * entendió lo escrito («= 785,15 Bs») y, si la moneda no lleva decimales,
   * deja solo enteros (como `step="1"`). Ver «CÓMO SE ENTENDIÓ», más abajo.
   */
  moneda?: FormatoMoneda | null;
};

export const Campo = forwardRef<HTMLInputElement, CampoProps>(function Campo(
  { className, error, type, onChange, min, max, step, inputMode, moneda, ...props },
  ref,
) {
  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ LAS CASILLAS DE NÚMERO SON DE TEXTO POR DENTRO
    ═══════════════════════════════════════════════════════════════════════════

    Un iPhone en español trae COMA en su teclado numérico y no punto, y un
    `<input type="number">` no acepta la coma: desde el teléfono no se podía
    escribir un precio con céntimos ni una tasa. Lo vio el dueño creando un
    producto.

    Así que se siguen pidiendo como `type="number"` —las pantallas no cambian—
    pero se dibujan como texto con teclado decimal, y lo tecleado pasa por
    `normalizarDecimal`: la coma se vuelve punto y las letras no entran. Arreglado
    aquí, queda arreglado en todas las pantallas a la vez.

    `min` ≥ 0 quita el signo menos, y `step="1"` deja solo enteros: son las dos
    cosas que el navegador hacía con la casilla de número y que se perderían.
  */
  const esNumero = type === "number";
  const permiteNegativo = esNumero && !(min !== undefined && Number(min) >= 0);

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ EN PESOS, LA CASILLA PONE LOS PUNTOS DE MILES (2026-10-30)
    ═══════════════════════════════════════════════════════════════════════════

    En una moneda sin decimales (COP) el punto solo puede ser de miles. Antes
    se quitaba al teclearlo y el dueño lo encontró raro: «en COP debería
    dejarme». Ahora, mientras escribe, la casilla enseña TAL CUAL lo tecleado
    («500.000», «500,000», «500000») y a la pantalla le llega siempre
    «500000». Al salir de la casilla queda ordenada: «500.000».

    (Un primer intento ponía los puntos a cada tecla: el punto recién
    tecleado desaparecía hasta la tercera cifra y parecía que no entraba.)
  */
  const agrupar = esNumero && moneda?.decimals === 0;
  const [escrito, setEscrito] = useState<string | null>(null);
  const cursorPendiente = useRef<number | null>(null);

  const cambiar =
    esNumero && onChange
      ? (e: ChangeEvent<HTMLInputElement>) => {
          const tecleado = e.target.value;
          const limpio = normalizarDecimal(tecleado, {
            negativo: permiteNegativo,
            decimales: moneda ? moneda.decimals !== 0 : !(step === 1 || step === "1"),
          });
          if (!agrupar) {
            if (limpio !== tecleado) e.target.value = limpio;
            onChange(e);
            return;
          }
          // Lo que se ve: las cifras con sus puntos y comas, sin letras.
          const visible = (permiteNegativo && tecleado.trim().startsWith("-") ? "-" : "") + tecleado.replace(/[^\d.,]/g, "");
          setEscrito(visible);
          // La pantalla recibe el número limpio. ⚠️ No se le devuelve a la
          // casilla lo tecleado aquí mismo: hay pantallas que leen
          // `e.target.value` más tarde (dentro de `setContados(c => …)`) y se
          // llevaban «500.» en vez de «500». Lo pone React al dibujar, y el
          // cursor se coloca después (ver `cursorPendiente`).
          if (e.target.selectionStart !== null) {
            cursorPendiente.current = e.target.selectionStart + visible.length - tecleado.length;
          }
          e.target.value = limpio;
          onChange(e);
        }
      : onChange;

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ EL TECLADO NUMÉRICO DEL IPHONE NO TIENE SIGNO MENOS (2026-10-12)
    ═══════════════════════════════════════════════════════════════════════════

    `inputMode="decimal"` saca en el iPhone un teclado de números y coma, sin
    «−». En las casillas que SÍ aceptan negativos —cuánto dice el banco cuando
    la cuenta está en rojo, una ganancia por debajo del costo— no había forma
    de escribirlo desde el teléfono. Lo encontró el dueño en un cierre de caja.

    Así que esas casillas llevan un botón «±» que cambia el signo, solo en
    pantallas táctiles (`pointer-coarse`): con teclado de verdad se escribe el
    «−» y el botón sobra. Las que no aceptan negativos (`min` ≥ 0) no lo llevan:
    allí un «−» no significaría nada.
  */
  const propio = useRef<HTMLInputElement | null>(null);
  const unirRef = useCallback(
    (el: HTMLInputElement | null) => {
      propio.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );
  const conBotonDeSigno = permiteNegativo && !props.disabled && !props.readOnly;

  /*
    ═══════════════════════════════════════════════════════════════════════════
     ⚠️ CÓMO SE ENTENDIÓ (2026-10-30)
    ═══════════════════════════════════════════════════════════════════════════

    «785.150» en bolívares puede ser 785 mil o 785 con quince, y Softfi lo lee
    como decimal. En una casilla de dinero se enseña, dentro y a la derecha,
    cómo quedó: «= 785,15 Bs». Solo cuando aporta (ver `lecturaDeMonto`).

    · Va DENTRO de la casilla y no debajo: aparecer y desaparecer debajo
      empujaría lo que sigue mientras el cajero escribe.
    · En una casilla angosta (las de las tablas) no cabe y no sale: se mide la
      casilla (`@container/campo`), no la pantalla.
    · La caja de afuera está siempre que haya `moneda`, aunque no haya nada
      que enseñar: si apareciera con la primera cifra, la casilla se
      volvería a crear y perdería el foco a media escritura. Por lo mismo
      basta con PASAR `moneda`, aunque sea `null` mientras llegan las monedas.
  */
  const conCaja = moneda !== undefined;
  const idLectura = useId();
  const lectura = moneda ? lecturaDeMonto(props.value as string | number | undefined, moneda) : null;

  // En pesos: lo tecleado mientras siga diciendo el mismo número (si la
  // pantalla cambió el valor por su cuenta —«Completar»—, manda el suyo).
  const mostrado = !agrupar
    ? props.value
    : escrito !== null &&
        normalizarDecimal(escrito, { negativo: permiteNegativo, decimales: false }) === String(props.value ?? "")
      ? escrito
      : conPuntosDeMiles(props.value);
  useLayoutEffect(() => {
    const el = propio.current;
    const cursor = cursorPendiente.current;
    cursorPendiente.current = null;
    if (el && cursor !== null && document.activeElement === el) el.setSelectionRange(cursor, cursor);
  });

  const invertirSigno = () => {
    const el = propio.current;
    if (!el) return;
    const nuevo = el.value.startsWith("-") ? el.value.slice(1) : `-${el.value}`;
    // El valor se pone como si se hubiera tecleado, para que el `onChange` de
    // la pantalla se entere igual que con cualquier otra tecla.
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set?.call(el, nuevo);
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const casilla = (
    <input
      ref={unirRef}
      {...props}
      type={esNumero ? "text" : type}
      inputMode={esNumero ? (inputMode ?? "decimal") : inputMode}
      autoComplete={props.autoComplete ?? (esNumero ? "off" : undefined)}
      min={esNumero ? undefined : min}
      max={esNumero ? undefined : max}
      step={esNumero ? undefined : step}
      value={mostrado}
      onChange={cambiar}
      onBlur={
        agrupar
          ? (e) => {
              setEscrito(null);
              props.onBlur?.(e);
            }
          : props.onBlur
      }
      aria-describedby={lectura ? cx(props["aria-describedby"], idLectura) : props["aria-describedby"]}
      style={
        lectura
          ? ({
              ...props.style,
              // Lo que ocupa la lectura, para que lo escrito no se le meta debajo.
              "--pr-lectura": `${lectura.length * 0.45 + 1.75}rem`,
              "--pr-lectura-tactil": `${lectura.length * 0.45 + 4.25}rem`,
            } as React.CSSProperties)
          : props.style
      }
      className={cx(
        BASE_CAMPO,
        (conBotonDeSigno || conCaja || !pidioSuAncho(className)) && "w-full",
        "h-11",
        conBotonDeSigno && "pointer-coarse:pr-14",
        lectura && "@min-[15rem]/campo:pr-(--pr-lectura)",
        lectura && conBotonDeSigno && "pointer-coarse:@min-[15rem]/campo:pr-(--pr-lectura-tactil)",
        error && "border-danger focus:ring-danger/25",
        conCaja ? sinLoDeLaCaja(className) : conBotonDeSigno ? sinAncho(className) : className,
      )}
    />
  );
  if (!conBotonDeSigno && !conCaja) return casilla;

  return (
    // El ancho que pidió la pantalla lo lleva la caja de afuera (y con
    // `moneda`, también lo que la acomoda en una fila: ver `loDeLaCaja`).
    <div
      className={cx(
        "relative",
        conCaja && "@container/campo",
        conCaja
          ? cx(!pidioSuAncho(className) && "w-full", loDeLaCaja(className))
          : pidioSuAncho(className)
            ? soloAncho(className)
            : "w-full",
      )}
    >
      {casilla}
      {lectura && (
        <span
          id={idLectura}
          className={cx(
            "pointer-events-none absolute inset-y-0 right-3.5 hidden items-center gap-1 whitespace-nowrap text-xs tabular",
            "animate-[softfi-aparecer_0.14s_ease-out] motion-reduce:animate-none @min-[15rem]/campo:flex",
            conBotonDeSigno && "pointer-coarse:right-14",
          )}
        >
          <span className="text-faint">=</span>
          <span className="font-medium text-ink-soft">{lectura}</span>
        </span>
      )}
      {conBotonDeSigno && (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Cambiar el signo"
          title="Positivo o negativo"
          // Sin robarle el foco a la casilla: el teclado del teléfono no se cierra.
          onMouseDown={(e) => e.preventDefault()}
          onClick={invertirSigno}
          className="absolute inset-y-1.5 right-1.5 hidden w-10 place-items-center rounded-[calc(var(--radius-control)-4px)] bg-surface-2 text-base font-semibold text-ink-soft active:bg-surface-hover pointer-coarse:grid"
        >
          ±
        </button>
      )}
    </div>
  );
});

/** Las clases de ancho («w-24», «sm:w-40») de una lista de clases. */
function soloAncho(className?: string): string {
  return (className ?? "").split(/\s+/).filter((c) => /^(?:[a-z0-9-]+:)*w-/.test(c)).join(" ");
}

/**
 * «500000» → «500.000», para la casilla de una moneda sin decimales. Lo que
 * venga del servidor con decimales («244875.0000») se enseña sin ellos.
 */
function conPuntosDeMiles(valor: InputHTMLAttributes<HTMLInputElement>["value"]): string | undefined {
  if (valor === undefined || valor === null) return undefined;
  const texto = String(valor);
  if (!/\d/.test(texto)) return texto;
  const numero = Number(texto);
  const entero = Number.isFinite(numero) ? String(Math.trunc(Math.abs(numero))) : texto.replace(/\D/g, "");
  const signo = texto.trim().startsWith("-") ? "-" : "";
  return signo + entero.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

/**
 * Las clases que acomodan la casilla entre sus vecinas —ancho, flex, rejilla,
 * márgenes— y que con `moneda` tiene que llevar la caja de afuera: puestas en
 * la casilla de dentro no harían nada.
 */
const DE_LA_CAJA =
  /^(?:[a-z0-9-]+:)*(?:w-|min-w-|max-w-|flex-|basis-|grow|shrink|self-|col-|row-|order-|justify-self-|place-self-|-?m[trblxyse]?-)/;

function loDeLaCaja(className?: string): string {
  return (className ?? "").split(/\s+/).filter((c) => DE_LA_CAJA.test(c)).join(" ");
}

function sinLoDeLaCaja(className?: string): string {
  return (className ?? "").split(/\s+/).filter((c) => c && !DE_LA_CAJA.test(c)).join(" ");
}

/** La lista de clases sin las de ancho: el ancho lo lleva la caja de afuera. */
function sinAncho(className?: string): string {
  return (className ?? "").split(/\s+/).filter((c) => !/^(?:[a-z0-9-]+:)*w-/.test(c)).join(" ");
}

export type AreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { error?: boolean };

export const Area = forwardRef<HTMLTextAreaElement, AreaProps>(function Area(
  { className, error, ...props },
  ref,
) {
  return (
    <textarea
      ref={ref}
      className={cx(
        BASE_CAMPO,
        !pidioSuAncho(className) && "w-full",
        "py-2.5 min-h-20",
        error && "border-danger",
        className,
      )}
      {...props}
    />
  );
});

/**
 * Un desplegable que se escribe igual que un `<select>`:
 *
 *     <Lista value={moneda} onChange={(e) => setMoneda(e.target.value)}>
 *       <option value="USD">Dólar</option>
 *     </Lista>
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR FUERA ES UN `<select>`; POR DENTRO, NO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo dibuja `Desplegable` (ver ese archivo): el `<select>` del navegador lo
 * pintaba Windows, y el dueño lo encontró —con razón— «genérico y feo».
 *
 * Pero se sigue escribiendo con `<option>` y leyendo `e.target.value`, a
 * propósito: son ochenta y cinco sitios, y cambiarles la cara SIN TOCAR NINGUNO
 * es lo que hace que el cambio no pueda romper una pantalla por un descuido.
 *
 * Lo que hacía el `<select>` y se respeta:
 *  · Una `<option value="" disabled>` es el aviso («Elige…»): no sale en la
 *    lista y es lo que se lee mientras no se elija nada.
 *
 *    ⚠️ Un «Elige…» de un campo obligatorio va SIEMPRE con `disabled`. Sin él
 *    sale en la lista como una opción más, y elegirlo solo sirve para deshacer
 *    lo elegido. Lo vio el dueño en «Con qué» del vuelto: «ese Elige está muy
 *    pronunciado». Las de verdad —«Todas las categorías», «Sin cuenta»,
 *    «Precio normal»— van sin `disabled`, porque se eligen. Lo vigila
 *    `Desplegable.test.tsx`.
 *  · Si `value` no es ninguna opción, se enseña la primera que se pueda
 *    elegir, como hace React. Ninguna pantalla cambia de comportamiento.
 *  · `onChange` solo cuando el valor cambia de verdad.
 */
export type ListaProps = {
  value?: string | number | null;
  onChange?: (e: ChangeEvent<HTMLSelectElement>) => void;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  autoFocus?: boolean;
  id?: string;
  title?: string;
  "aria-label"?: string;
};

export const Lista = forwardRef<HTMLButtonElement, ListaProps>(function Lista(
  { value, onChange, children, ...resto },
  ref,
) {
  const { opciones, aviso } = leerOpciones(children);
  const valor = value == null ? "" : String(value);

  return (
    <Desplegable
      ref={ref}
      valor={valor}
      opciones={opciones}
      siNoCoincide={aviso !== undefined && valor === "" ? "texto" : "primera"}
      textoSinElegir={aviso}
      onElegir={(elegido) => onChange?.(comoEventoDeSelect(elegido))}
      {...resto}
    />
  );
});

/** Las `<option>` que vienen como hijos, incluidas las de un `.map` o un `<>`. */
function leerOpciones(
  hijos: ReactNode,
  salida: { opciones: OpcionDesplegable[]; aviso?: string } = { opciones: [] },
) {
  Children.forEach(hijos, (hijo) => {
    if (!isValidElement(hijo)) return;
    const p = hijo.props as {
      value?: string | number;
      disabled?: boolean;
      hidden?: boolean;
      label?: string;
      children?: ReactNode;
    };
    if (hijo.type === Fragment || hijo.type === "optgroup") {
      leerOpciones(p.children, salida);
      return;
    }
    if (hijo.type !== "option" || p.hidden) return;

    const etiqueta = p.label ?? textoDe(p.children).replace(/\s+/g, " ").trim();
    // Sin `value`, el `<select>` usa el texto de la opción. Igual aquí.
    const valor = p.value == null ? etiqueta : String(p.value);
    if (p.disabled && valor === "") {
      salida.aviso = etiqueta;
      return;
    }
    salida.opciones.push({ valor, etiqueta, desactivada: Boolean(p.disabled) });
  });
  return salida;
}

function textoDe(nodo: ReactNode): string {
  if (nodo == null || typeof nodo === "boolean") return "";
  if (typeof nodo === "string" || typeof nodo === "number") return String(nodo);
  if (Array.isArray(nodo)) return nodo.map(textoDe).join("");
  if (isValidElement(nodo)) return textoDe((nodo.props as { children?: ReactNode }).children);
  return "";
}

/**
 * Lo que recibe `onChange`: lo justo para que `e.target.value` funcione como
 * con un `<select>` de verdad, que es lo único que leen las 85 pantallas.
 */
function comoEventoDeSelect(valor: string): ChangeEvent<HTMLSelectElement> {
  const objetivo = { value: valor } as HTMLSelectElement;
  return {
    target: objetivo,
    currentTarget: objetivo,
    preventDefault() {},
    stopPropagation() {},
  } as unknown as ChangeEvent<HTMLSelectElement>;
}

/**
 * Envoltorio de un campo: etiqueta, ayuda y mensaje de error.
 * Usar siempre esto en vez de una etiqueta suelta: así el error aparece
 * exactamente debajo del campo que lo causó.
 */
export function Etiquetado({
  label,
  ayuda,
  error,
  requerido,
  children,
  className,
}: {
  label: string;
  ayuda?: string;
  error?: string;
  requerido?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    // ⚠️ `min-w-0`: dentro de una rejilla sin columnas fijas (lo normal en el
    // celular), un desplegable con una opción larga —«USD — Dólar
    // estadounidense (tus precios)»— estiraba la columna y se salía de su
    // recuadro. El `<select>` de antes se encogía solo; el botón de
    // `Desplegable` no, así que se le da permiso aquí y el texto se recorta.
    <label className={cx("block min-w-0", className)}>
      <span className="mb-1.5 flex items-center gap-1 text-sm font-medium text-ink-soft">
        {label}
        {requerido && <span className="text-danger">*</span>}
      </span>
      {children}
      {error ? (
        <span className="mt-1.5 block text-xs text-danger">{error}</span>
      ) : ayuda ? (
        <span className="mt-1.5 block text-xs text-muted">{ayuda}</span>
      ) : null}
    </label>
  );
}

/** Interruptor de encendido/apagado. Más claro que una casilla en celular. */
/**
 * ELEGIR VARIOS DE UNA LISTA CORTA: «✓ Número de referencia» · «+ Banco».
 *
 * Es la pastilla de Métodos de pago, sacada aparte el 2026-09-28: el dueño la
 * señaló como «la de Softfi» y pidió que las casillas sueltas dentro de un
 * recuadro (las sucursales al importar, las monedas del catálogo web) se
 * vieran así. Marcada va llena con ✓; sin marcar, en blanco con +: se entiende
 * de un vistazo qué está puesto y qué se puede añadir.
 *
 * Para elegir UNA opción entre varias está `Segmentado`; para encender una
 * cosa con su explicación, `Interruptor`.
 */
export function Pastilla({
  activa,
  onClick,
  children,
  disabled,
  title,
}: {
  activa: boolean;
  onClick: () => void;
  children: ReactNode;
  disabled?: boolean;
  /** La explicación al dejar el ratón encima. */
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      disabled={disabled}
      title={title}
      className={cx(
        "pulsable-suave inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors disabled:cursor-default",
        activa
          ? "border-accent bg-accent text-on-accent"
          : "border-line bg-surface text-ink-soft hover:border-accent/50",
        disabled && !activa && "opacity-50",
      )}
    >
      {activa ? <Check size={12} aria-hidden="true" /> : <Plus size={12} aria-hidden="true" />}
      {children}
    </button>
  );
}

/**
 * ELEGIR VARIOS, CUANDO CADA UNO TRAE SU DETALLE: la Pastilla en grande.
 *
 * Nació el 2026-10-14 para los grupos de extras de un plato. El dueño: «esos
 * selectores son muy genéricos, no representan a Softfi». Eran casillas del
 * navegador dentro de un recuadro. Ahora toda la tarjeta se pulsa y lleva el
 * mismo ✓ / + de la `Pastilla`: llena con ✓ si está puesta, en blanco con +
 * si se puede añadir.
 *
 * Para una lista corta de nombres sueltos sigue siendo la `Pastilla`.
 */
export function TarjetaElegible({
  activa,
  onClick,
  titulo,
  children,
  disabled,
}: {
  activa: boolean;
  onClick: () => void;
  titulo: ReactNode;
  /** El detalle debajo del título: qué trae, cuánto cuesta. */
  children?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      disabled={disabled}
      className={cx(
        "pulsable-suave flex w-full items-start gap-3 rounded-[var(--radius-card)] border p-3.5 text-left transition-colors disabled:cursor-default disabled:opacity-60",
        activa
          ? "border-accent bg-accent-soft/40"
          : "border-line bg-surface hover:border-accent/50 hover:bg-surface-2/50",
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-ink">{titulo}</span>
        {children && <span className="mt-1.5 block">{children}</span>}
      </span>
      <span
        aria-hidden="true"
        className={cx(
          "mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border transition-colors",
          activa ? "border-accent bg-accent text-on-accent" : "border-line-strong text-muted",
        )}
      >
        {activa ? <Check size={14} /> : <Plus size={14} />}
      </span>
    </button>
  );
}

/**
 * Un interruptor de una función del negocio.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ ENCENDER ALGO SIN DECIR DÓNDE SE USA ES DEJARLO A MEDIAS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Pasó de verdad: el dueño encendió «Comisiones a empleados», volvió a la
 * pantalla anterior y no encontró en ningún sitio dónde escribir el
 * porcentaje. Está en Equipo, dentro de cada persona —que es donde tiene que
 * estar, porque un empleado puede cobrar más que otro—, pero eso no se adivina.
 *
 * El resultado no es «una función que cuesta encontrar»: es una función
 * encendida que **no hace nada**, y un dueño convencido de que el programa
 * está roto.
 *
 * Por eso `dondeSeConfigura` aparece solo cuando el interruptor está
 * ENCENDIDO: apagado no hay nada que configurar y sería ruido en una lista que
 * ya es larga.
 */
export function Interruptor({
  activo,
  onChange,
  label,
  descripcion,
  disabled,
  dondeSeConfigura,
}: {
  activo: boolean;
  onChange: (valor: boolean) => void;
  label: string;
  descripcion?: string;
  disabled?: boolean;
  /** A dónde ir a usarla. Se enseña solo con el interruptor encendido. */
  dondeSeConfigura?: { texto: string; a: string };
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-ink">{label}</p>
        {descripcion && <p className="mt-0.5 text-xs text-muted">{descripcion}</p>}
        {activo && dondeSeConfigura && (
          <Link
            to={dondeSeConfigura.a}
            className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline"
          >
            {dondeSeConfigura.texto}
            <ArrowRight size={13} />
          </Link>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={activo}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!activo)}
        className={cx(
          "relative h-6 w-11 shrink-0 rounded-full transition-colors",
          "focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
          activo ? "bg-accent" : "bg-line-strong",
          disabled && "cursor-not-allowed opacity-50",
        )}
      >
        {/*
          El círculo se mueve con `translate-x` en valores ENTEROS.

          ⚠️ Tailwind solo genera fracciones para 0.5, 1.5, 2.5 y 3.5. Un
          `translate-x-5.5` no existe: no produce CSS, el transform queda en
          `none` y el círculo se queda quieto en la izquierda aunque el
          interruptor esté encendido. Pasó exactamente eso.

          Cuentas: pista 44px − círculo 20px − 2px de margen a cada lado = 20px
          de recorrido, que es justo `translate-x-5`.
        */}
        <span
          className={cx(
            "absolute left-0.5 top-0.5 size-5 rounded-full bg-white shadow-sm transition-transform duration-150",
            activo ? "translate-x-5" : "translate-x-0",
          )}
        />
      </button>
    </div>
  );
}

// =============================================================================
//  CONTENEDORES
// =============================================================================

export function Tarjeta({
  children,
  className,
  padding = true,
}: {
  children: ReactNode;
  className?: string;
  padding?: boolean;
}) {
  return (
    <div
      className={cx(
        "rounded-[var(--radius-card)] border border-line bg-surface shadow-[var(--shadow-card)]",
        // Más aire por dentro: es lo que separa una pantalla "densa de datos" de
        // una que se lee tranquilo. En celular se recorta para no desperdiciar.
        padding && "p-4 sm:p-6",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function TituloSeccion({
  titulo,
  descripcion,
  accion,
}: {
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {/* `titulo-seccion` vive en index.css: una sola definición para toda la
            app, en vez de que cada pantalla elija su tamaño a ojo. */}
        <h2 className="titulo-seccion">{titulo}</h2>
        {descripcion && <p className="mt-1.5 text-sm leading-relaxed text-muted">{descripcion}</p>}
      </div>
      {accion}
    </div>
  );
}

// =============================================================================
//  INDICADORES
// =============================================================================

type TonoEtiqueta = "neutro" | "exito" | "aviso" | "peligro" | "acento";

const TONOS: Record<TonoEtiqueta, string> = {
  neutro: "bg-surface-2 text-muted border-line",
  exito: "bg-success-soft text-success border-success/30",
  aviso: "bg-warning-soft text-warning border-warning/30",
  peligro: "bg-danger-soft text-danger border-danger/30",
  acento: "bg-accent-soft text-accent border-accent/30",
};

/**
 * Una etiqueta corta: «Vencido», «2 Por revisar», «Aquí estás».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ LA MAYÚSCULA SE PONE AQUÍ, NO EN CADA SITIO QUE ESCRIBE UNA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Estas etiquetas estaban repartidas por veinte pantallas y casi todas en
 * minúscula: «vencido», «sin ventas», «cada mes», «3 en cocina». Y una etiqueta en
 * minúscula, en el software al que le confías la caja, no se lee como una
 * decisión de estilo: se lee como que nadie revisó.
 *
 * Repasarlas a mano arregla las de hoy y no arregla la que se escriba mañana.
 * Aquí se arreglan todas, incluidas las que aún no existen.
 *
 * Se usa `comoEtiqueta` y no `conMayuscula` porque muchas empiezan por un
 * número —«2 por revisar»— y subir el primer carácter no haría nada.
 */
export function Insignia({
  children,
  tono = "neutro",
  className,
  title,
}: {
  children: ReactNode;
  tono?: TonoEtiqueta;
  className?: string;
  /**
   * Qué significa esta insignia, al pasar el ratón.
   *
   * ⚠️ Una insignia es una palabra suelta, y una palabra suelta se malentiende:
   * «dormido» hubo que preguntar qué era. El globo del navegador lo resuelve
   * sin ocupar sitio en una lista que ya va apretada.
   */
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cx(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap",
        TONOS[tono],
        className,
      )}
    >
      {conLaPrimeraEnMayuscula(children)}
    </span>
  );
}

/**
 * Sube la primera letra de una etiqueta, venga como venga.
 *
 * ⚠️ No basta con mirar si `children` es un texto. La mitad de las etiquetas se
 * escriben con una cifra delante —`{n} variantes`, `{cuantas} por revisar`— y
 * entonces React entrega una LISTA: `[3, " variantes"]`. Mirando solo el primer
 * elemento no se toca nada, y esas —las que llevan número— eran justo las que
 * seguían en minúscula.
 *
 * Así que se busca el primer trozo de texto que tenga una letra y se sube esa.
 */
function conLaPrimeraEnMayuscula(children: ReactNode): ReactNode {
  if (typeof children === "string") return comoEtiqueta(children);
  if (!Array.isArray(children)) return children;

  let hecho = false;
  return children.map((hijo, i) => {
    if (hecho || typeof hijo !== "string" || !/\p{L}/u.test(hijo)) return hijo;
    hecho = true;
    return <span key={i}>{comoEtiqueta(hijo)}</span>;
  });
}

export function Cargando({ texto = "Cargando…" }: { texto?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted">
      <Loader2 size={18} className="animate-spin" />
      {texto}
    </div>
  );
}

/** Rectángulos grises mientras cargan los datos. Se percibe más rápido que un spinner. */
export function Esqueleto({ filas = 5 }: { filas?: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      {Array.from({ length: filas }).map((_, i) => (
        <div key={i} className="h-14 animate-pulse rounded-[var(--radius-control)] bg-surface-2" />
      ))}
    </div>
  );
}

/**
 * Pantalla vacía. Nunca dejar una lista vacía sin explicación: el usuario no
 * sabe si está vacía o si la app falló. Siempre decir qué es y qué hacer.
 */
export function Vacio({
  icono,
  titulo,
  descripcion,
  accion,
}: {
  icono?: ReactNode;
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
      {icono && (
        <div className="mb-4 flex size-14 items-center justify-center rounded-[var(--radius-card)] bg-surface-2 text-muted">
          {icono}
        </div>
      )}
      <h3 className="text-base font-semibold text-ink">{titulo}</h3>
      {descripcion && <p className="mt-1.5 max-w-sm text-sm text-muted">{descripcion}</p>}
      {accion && <div className="mt-5">{accion}</div>}
    </div>
  );
}

/**
 * Aviso en línea, para explicar algo dentro de un formulario.
 *
 * Lleva una barra de color en el borde izquierdo. No es adorno: un bloque de
 * color plano se confunde con "una tarjeta más", mientras que la barra lateral
 * es la señal universal de "esto es una nota sobre lo de al lado". Y el texto va
 * a 13 px y no a 12: estas notas se leen de verdad, no son letra pequeña.
 */
export function Nota({
  children,
  tono = "acento",
  icono,
  className,
}: {
  children: ReactNode;
  tono?: TonoEtiqueta;
  icono?: ReactNode;
  className?: string;
}) {
  const fondos: Record<TonoEtiqueta, string> = {
    neutro: "bg-surface-2 text-ink-soft border-l-line-strong",
    exito: "bg-success-soft text-success border-l-success",
    aviso: "bg-warning-soft text-warning border-l-warning",
    peligro: "bg-danger-soft text-danger border-l-danger",
    acento: "bg-accent-soft text-accent border-l-accent",
  };
  return (
    <div
      className={cx(
        "flex items-start gap-2.5 border-l-[3px] p-3 pl-3 text-[13px]",
        "rounded-r-[var(--radius-control)] rounded-l-[3px]",
        fondos[tono],
        className,
      )}
    >
      {icono && <span className="mt-0.5 shrink-0">{icono}</span>}
      <div className="min-w-0 leading-relaxed">{children}</div>
    </div>
  );
}

// =============================================================================
//  ENCABEZADO DE PÁGINA
// =============================================================================

/**
 * El título de una pantalla, con su descripción, su acción y su enlace de vuelta.
 *
 * Antes cada pantalla lo escribía a mano y ninguna coincidía con otra: unas
 * separaban 4, otras 5; unas ponían el botón arriba y otras abajo. Con esto, las
 * 21 pantallas comparten la misma estructura y el ojo no tiene que recolocarse
 * cada vez que se navega.
 */
export function EncabezadoPagina({
  titulo,
  descripcion,
  accion,
  volver,
  junto,
  ayuda,
}: {
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
  /** Algo pequeño al lado del título: de qué sucursal son las cifras. */
  junto?: ReactNode;
  /** `{ a: "/ajustes", texto: "Ajustes" }` para las pantallas de segundo nivel. */
  volver?: { a: string; texto: string };
  /**
   * El botón «Ayuda» (2026-10-26) sale solo si la dirección tiene ayuda (ver
   * `features/ayuda/rutas.ts`). `false` lo quita —un encabezado dentro de otra
   * pantalla que ya tiene el suyo— y un texto elige otro tema.
   */
  ayuda?: boolean | string;
}) {
  const botonDeAyuda = ayuda === false ? null : <BotonDeAyuda tema={typeof ayuda === "string" ? ayuda : undefined} />;
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
      <div className="min-w-0">
        {volver && (
          <a
            href={volver.a}
            className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted transition-colors hover:text-ink"
          >
            <ChevronLeft size={15} />
            {volver.texto}
          </a>
        )}
        {junto ? (
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="titulo-pagina">{titulo}</h1>
            {junto}
          </div>
        ) : (
          <h1 className="titulo-pagina">{titulo}</h1>
        )}
        {descripcion && (
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-muted">{descripcion}</p>
        )}
      </div>
      {/* `max-w-full`: con cuatro botones (Clientes, 2026-10-05) la fila no
          cabía en el celular y la página entera se desplazaba de lado; con el
          tope, los botones bajan a otra línea. */}
      {/* La ayuda va la última: arriba a la derecha, en la esquina. */}
      {/* Sin acciones propias, solo queda la ayuda, que en el celular va en la
          barra de arriba: entonces la caja ni se dibuja allí. */}
      <div className={cx("max-w-full shrink-0 flex-wrap gap-2 empty:hidden", accion ? "flex" : "hidden lg:flex")}>
        {accion}
        {botonDeAyuda}
      </div>
    </div>
  );
}

// =============================================================================
//  MÉTRICA — la cifra grande
// =============================================================================

type TonoMetrica = "neutro" | "exito" | "peligro" | "acento";

/**
 * Una cifra con su rótulo. Es la pieza del dashboard, del arqueo de caja y de
 * los reportes.
 *
 * Tres decisiones de diseño que la hacen legible de lejos —que es como se mira
 * un dashboard, no leyendo palabra por palabra:
 *
 *   1. **El rótulo va ARRIBA y pequeño, la cifra abajo y grande.** Al revés
 *      (cifra arriba) el ojo tiene que subir para saber qué está leyendo.
 *   2. **La cifra usa números de ancho fijo.** Si no, "111" y "888" ocupan
 *      distinto y una fila de métricas queda desalineada.
 *   3. **El color solo se usa cuando SIGNIFICA algo** (verde entra, rojo falta).
 *      Colorear todas las cifras convierte el color en decoración y deja de
 *      avisar de nada.
 */
export function Metrica({
  rotulo,
  valor,
  detalle,
  tono = "neutro",
  icono,
  className,
}: {
  rotulo: string;
  valor: ReactNode;
  detalle?: ReactNode;
  tono?: TonoMetrica;
  icono?: ReactNode;
  className?: string;
}) {
  const colorCifra: Record<TonoMetrica, string> = {
    neutro: "text-ink",
    exito: "text-success",
    peligro: "text-danger",
    acento: "text-accent",
  };

  return (
    <div
      className={cx(
        "rounded-[var(--radius-card)] border border-line bg-surface p-4 shadow-[var(--shadow-card)]",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="rotulo">{rotulo}</p>
        {icono && <span className="shrink-0 text-faint">{icono}</span>}
      </div>
      <p className={cx("cifra mt-2 truncate", colorCifra[tono])}>{valor}</p>
      {detalle && <div className="mt-1.5 text-xs leading-relaxed text-muted">{detalle}</div>}
    </div>
  );
}

// =============================================================================
//  FILA DE DATO — "etiqueta ............ valor"
// =============================================================================

/**
 * La fila de un desglose. Se repite en el arqueo, en el recibo, en los totales.
 *
 * El valor va con números de ancho fijo y alineado a la derecha: así una columna
 * de importes se lee de un vistazo en vez de número por número.
 */
export function FilaDato({
  etiqueta,
  valor,
  ayuda,
  tono = "neutro",
  fuerte,
}: {
  etiqueta: ReactNode;
  valor: ReactNode;
  ayuda?: string;
  tono?: TonoMetrica;
  /** Para la línea del total, que tiene que destacar sobre las de arriba. */
  fuerte?: boolean;
}) {
  const color: Record<TonoMetrica, string> = {
    neutro: fuerte ? "text-ink" : "text-ink-soft",
    exito: "text-success",
    peligro: "text-danger",
    acento: "text-accent",
  };

  return (
    <div
      className={cx(
        "flex items-baseline justify-between gap-4 py-2",
        fuerte && "border-t border-line-strong pt-3 mt-1",
      )}
    >
      <div className="min-w-0">
        <span className={cx("text-sm", fuerte ? "font-semibold text-ink" : "text-muted")}>
          {etiqueta}
        </span>
        {ayuda && <p className="mt-0.5 text-xs text-faint">{ayuda}</p>}
      </div>
      <span
        className={cx(
          "shrink-0 text-right tabular",
          fuerte ? "text-base font-semibold" : "text-sm font-medium",
          color[tono],
        )}
      >
        {valor}
      </span>
    </div>
  );
}

// =============================================================================
//  CONTROL SEGMENTADO — para filtros de pocas opciones
// =============================================================================

/**
 * Un grupo de botones donde solo uno está activo: "Hoy | Semana | Mes".
 *
 * Se usa en vez de una lista desplegable cuando hay 2-4 opciones. Con pocas
 * opciones, un desplegable esconde información y cuesta dos toques; esto muestra
 * todo y cuesta uno — que en una tablet de mostrador es la diferencia.
 */
export function Segmentado<T extends string>({
  valor,
  onChange,
  opciones,
  className,
  etiquetaAria,
}: {
  valor: T;
  onChange: (v: T) => void;
  opciones: { valor: T; etiqueta: string }[];
  className?: string;
  /**
   * Qué se está eligiendo aquí: «Período», «Moneda»…
   *
   * ⚠️ Importa cuando hay dos de estos en la misma pantalla. En Reportes
   * conviven el selector de período y la barra de secciones, y sin etiqueta un
   * lector de pantalla anuncia «lista de pestañas» dos veces sin decir de qué
   * es cada una.
   */
  etiquetaAria?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={etiquetaAria}
      className={cx(
        "inline-flex items-center gap-0.5 rounded-[var(--radius-control)] border border-line bg-surface-2 p-1",
        className,
      )}
    >
      {opciones.map((o) => {
        const activo = o.valor === valor;
        return (
          <button
            key={o.valor}
            type="button"
            role="tab"
            aria-selected={activo}
            onClick={() => onChange(o.valor)}
            className={cx(
              "rounded-[calc(var(--radius-control)-3px)] px-3 py-1.5 text-sm font-medium transition-colors",
              activo
                ? "bg-surface text-ink shadow-[var(--shadow-card)]"
                : "text-muted hover:text-ink",
            )}
          >
            {o.etiqueta}
          </button>
        );
      })}
    </div>
  );
}
